import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import type {
  ChartComponentSpec,
  MetricOption,
  SubjectEntity,
  ProcessedWidgetData,
} from "../../Processing/types";
import { ConfigPanelLayout } from "../shared/ConfigPanelLayout";
import { useConfiguratorSync } from "../shared/useConfiguratorSync";
import { PlotContextProvider } from "./PlotContext";
import { PlotConfigAccordion } from "./PlotConfigAccordion";
import { PlotPreviewPanel } from "./PlotPreviewPanel";
import { PlotPreviewBuilder } from "./PlotPreviewBuilder";
import { useControllers } from "../../Contexts/dashboardHooks";
import {
  plotReducer,
  createInitialState,
  hydrateFromSpec,
  formStateToSpec,
  type PlotFormState,
} from "./plotState";

interface PlotConfiguratorProps {
  onSpecChange: (spec: ChartComponentSpec | null) => void;
  existingSpec?: ChartComponentSpec;
  initialFormState?: unknown;
  metrics: MetricOption[];
  getSubjectEntities: (subjectType: string) => SubjectEntity[];
}

export function PlotConfigurator({
  onSpecChange,
  existingSpec,
  initialFormState,
  metrics,
  getSubjectEntities,
}: PlotConfiguratorProps) {
  const allPlayers: any[] = useSelector(({ team }: any) => team.allPlayers);

  const hydrate = useCallback(
    (spec: ChartComponentSpec) => hydrateFromSpec(spec, metrics),
    [metrics],
  );

  const [state, dispatch] = useConfiguratorSync({
    reducer: plotReducer,
    existingSpec,
    hydrate,
    createInitial: createInitialState,
    initialState: initialFormState as PlotFormState | undefined,
    toSpec: formStateToSpec,
    onSpecChange,
    metrics,
    getSubjectEntities,
  });

  const controllers = useControllers();

  const previewBuilder = useMemo(
    () =>
      new PlotPreviewBuilder(
        state.graphs,
        metrics,
        state.title,
        state.showLegend,
        state.inverted,
        state.showAllDatesInRange,
        state.groupBy,
        state.sortBy,
        state.sortSeriesId,
        state.sortIncludeStacked,
        getSubjectEntities,
        allPlayers,
        controllers,
      ),
    [
      state.graphs,
      metrics,
      state.title,
      state.showLegend,
      state.inverted,
      state.showAllDatesInRange,
      state.groupBy,
      state.sortBy,
      state.sortSeriesId,
      state.sortIncludeStacked,
      getSubjectEntities,
      allPlayers,
      controllers,
    ],
  );

  const previewData = useMemo<ProcessedWidgetData | null>(
    () => previewBuilder.build(),
    [previewBuilder],
  );

  const ctxValue = useMemo(
    () => ({ state, dispatch, metrics, getSubjectEntities }),
    [state, dispatch, metrics, getSubjectEntities],
  );

  return (
    <PlotContextProvider value={ctxValue}>
      <ConfigPanelLayout
        form={<PlotConfigAccordion />}
        preview={<PlotPreviewPanel previewData={previewData} />}
      />
    </PlotContextProvider>
  );
}
